import { ArrowBack } from '@mui/icons-material';
import type {
  ButtonProps,
  DialogContentProps,
  DialogProps,
  DialogTitleProps,
  TypographyProps,
} from '@mui/material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { dialogSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { CloseBig, VyIcon } from '@vy/ui-design-system';
import { isNotNullOrUndefined } from '@vy/utils';
import { merge, omit } from 'lodash';
import type { FC, MouseEventHandler, PropsWithChildren, ReactNode } from 'react';

type VyDialogProps = PropsWithChildren<{
  open: boolean;
  allowPropagation?: boolean;
  label?: ReactNode;
  labelDescription?: ReactNode;
  labelDescriptionProps?: TypographyProps;
  warningText?: ReactNode;
  cancelButtonText?: ReactNode;
  ctaText?: ReactNode;
  ctaDisabled?: boolean;
  onSubmit?: MouseEventHandler<HTMLButtonElement>;
  cancelButtonProps?: ButtonProps;
  ctaButtonProps?: ButtonProps;
  dialogTitleProps?: DialogTitleProps;
  warningColor?: string;
  dialogContentProps?: DialogContentProps;
  disableDividers?: boolean;
  navigateBack?: () => void;
}> &
  Omit<DialogProps, 'title'>;

export const VyDialog: FC<VyDialogProps> = ({
  open,
  onClose,
  label,
  labelDescription,
  warningText,
  cancelButtonText,
  ctaText,
  ctaDisabled,
  onSubmit,
  warningColor,
  children,
  cancelButtonProps,
  ctaButtonProps,
  dialogTitleProps,
  dialogContentProps,
  disableDividers = false,
  allowPropagation = false,
  navigateBack,
  labelDescriptionProps,
  ...dialogProps
}) => {
  const isDisabled = ctaDisabled ?? false;
  const shouldRenderButtons = isNotNullOrUndefined(cancelButtonText) || isNotNullOrUndefined(ctaText);

  const preventPropagation = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      onClick={allowPropagation ? undefined : preventPropagation}
      {...dialogProps}
      onClose={onClose}
      slotProps={{
        paper: {
          ...dialogProps.slotProps?.paper,
          sx: { borderRadius: '8px', ...(dialogProps.slotProps?.paper as { sx?: object } | undefined)?.sx },
        }
      }}
    >
      <DialogTitle
        {...dialogTitleProps}
        sx={{
          padding: '20px',
          position: 'relative',
          ...dialogTitleProps?.sx,
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: "5px"
          }}>
          {isNotNullOrUndefined(navigateBack) && (
            <IconButton onClick={navigateBack} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
          )}

          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 500,
              lineHeight: "24px"
            }}>
            {label}
          </Typography>

          <Box sx={{
            flex: 1
          }} />
          <IconButton
            onClick={(event) => {
              onClose?.(event, 'backdropClick');
            }}
            sx={{ padding: 0 }}
          >
            <CloseBig />
          </IconButton>
        </Stack>

        {labelDescription && (
          <Typography
            variant="body1"
            {...labelDescriptionProps}
            sx={[{
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "24px",
              ml: isNotNullOrUndefined(navigateBack) ? 6 : 0,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word'
            }, ...(Array.isArray(labelDescriptionProps?.sx) ? labelDescriptionProps.sx : labelDescriptionProps?.sx ? [labelDescriptionProps.sx] : [])]}>

            {labelDescription}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent
        dividers={!disableDividers}
        style={merge(
          { padding: '20px', minWidth: dialogProps.maxWidth !== 'sm' ? '680px' : 'auto' },
          dialogContentProps?.style ?? {},
        )}
        {...omit(dialogContentProps, 'style')}
      >
        {children}

        <DialogContentText>
          {warningText && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: "24px"
              }}>
              <VyIcon icon="Warning" color={warningColor ?? 'black'} />
              <Typography color={warningColor ?? undefined}>{warningText}</Typography>
            </Box>
          )}
        </DialogContentText>
      </DialogContent>
      {shouldRenderButtons && (
        <DialogActions
          style={{
            padding: '24px',
            gap: '5px',
          }}
        >
          {isNotNullOrUndefined(cancelButtonText) && (
            <Button
              {...getSelectorAttribute(dialogSelectors.cancelButton)}
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => {
                onClose?.({}, 'backdropClick');
              }}
              {...cancelButtonProps}
            >
              {cancelButtonText}
            </Button>
          )}

          {isNotNullOrUndefined(ctaText) && (
            <Button
              {...getSelectorAttribute(dialogSelectors.submitButton)}
              variant="contained"
              color="primary"
              size="large"
              disabled={isDisabled}
              onClick={onSubmit}
              sx={{ background: '#07105F' }}
              {...ctaButtonProps}
            >
              {ctaText}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};
